import { Subject } from "rxjs";

import type { KVStorage } from "@mirage/storage";

export interface KVStorageShape {
  actionSettings: {
    /** User settings for each action, not persisted to the server */
    settings: AssistActionSettingsMap;
    /** User settings for the action bar, not persisted to the server */
    actionBarSettings: AssistActionBarSettings;
    /** Version number tracked in case of backward-incompatible changes */
    version: 1;
  };
}

// ALL SETTINGS

export interface AssistActionsSettings
  extends AssistActionsSettingsInMemory,
    AssistActionsSettingsPersisted {}

// PERSISTED SETTINGS

export interface AssistActionsSettingsPersisted {
  actionSettings: AssistActionSettingsMap;
  actionBarSettings: AssistActionBarSettings;
}

// per-action settings map
export type AssistActionSettingsMap = {
  [actionId: string]: AssistActionSettings;
};

// per-action settings
export interface AssistActionSettings {
  isEnabled?: boolean;
  // TODO: add things like ordering via things like sortKey with generateKeyBetween
}

// global action bar settings
export interface AssistActionBarSettings {
  visibility?: AssistActionBarSettingsVisibility;
  position?: AssistActionBarSettingsPosition;
}

// action bar visibility
export type AssistActionBarSettingsVisibility =
  | {
      type: "visible";
    }
  | {
      // DEPRECATED: treated as default
      type: "snooze";
      untilTS: number;
    }
  | {
      type: "hidden";
    }
  | {
      // DEPRECATED: treated as default
      type: "auto-hide";
    };

// positioning setting of the action bar
export type AssistActionBarSettingsPosition = {
  type: "anchor";
  side: "left" | "right"; // side of screen the action bar is anchored on
  bottomPercent: number; // percentage from bottom of screen
};

// IN MEMORY SETTINGS

export interface AssistActionsSettingsInMemory {
  perTabSettings: AssistActionsPerTabSettingsMap;
}

// per-tab settings map
export interface AssistActionsPerTabSettingsMap {
  [tabId: number]: AssistActionsPerTabSettings;
}

// per-tab settings
export interface AssistActionsPerTabSettings {
  disabled?: boolean;
}

// TODO:(rich) clean up settings for actions that no longer exist

async function load(
  store: KVStorage<KVStorageShape>,
): Promise<AssistActionsSettingsPersisted> {
  const actionSettings = await store.get("actionSettings");
  if (actionSettings?.version === 1) {
    return {
      actionSettings: actionSettings.settings || {},
      actionBarSettings: actionSettings.actionBarSettings || {},
    };
  } else {
    // Ignore old data if the version is not >=1
    return {
      actionSettings: {},
      actionBarSettings: {},
    };
  }
}

export function createAssistActionsSettings(
  storage: KVStorage<KVStorageShape>,
) {
  /* subject that emits latest items, includes presets */
  const subject = new Subject<AssistActionsSettings>();

  let inMemorySettings: AssistActionsSettingsInMemory = {
    perTabSettings: {},
  };

  const getAllAssistActionSettings =
    async (): Promise<AssistActionsSettings> => {
      const persistedSettings = await load(storage);
      return {
        ...persistedSettings,
        ...inMemorySettings,
      };
    };

  const saveAndEmit = async (settings: AssistActionsSettings) => {
    await storage.set("actionSettings", {
      settings: settings.actionSettings,
      actionBarSettings: settings.actionBarSettings,
      version: 1,
    });
    inMemorySettings = {
      perTabSettings: settings.perTabSettings,
    };
    subject.next(settings);
  };

  const saveAssistActionSetting = async (
    actionId: string,
    setting: AssistActionSettings,
  ) => {
    const settings = await load(storage);
    settings.actionSettings[actionId] = setting;
    await saveAndEmit({
      ...settings,
      ...inMemorySettings,
    });
  };

  const saveAssistActionBarSetting = async (
    newSettings: Partial<AssistActionBarSettings>,
  ) => {
    const settings = await load(storage);
    settings.actionBarSettings = {
      ...settings.actionBarSettings,
      ...newSettings,
    };
    await saveAndEmit({
      ...settings,
      ...inMemorySettings,
    });
  };

  const saveAssistActionsPerTabSettings = async (
    tabId: number,
    setting: AssistActionsPerTabSettings,
  ) => {
    const settings = await load(storage);
    inMemorySettings.perTabSettings[tabId] = setting;
    await saveAndEmit({
      ...settings,
      ...inMemorySettings,
    });
  };

  return {
    getAllAssistActionSettings,
    saveAssistActionSetting,
    saveAssistActionBarSetting,
    saveAssistActionsPerTabSettings,
    assistActionSettingsObservable: () => subject.asObservable(),
  };
}
